import { useState } from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { requestPasswordReset } from "@/integrations/backend/auth";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await requestPasswordReset(email);
      setIsSuccess(true);
      toast.success("If an account exists with that email, a password reset link has been sent.");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset link. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <MarketingHeader />
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4 pt-24">
        <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-white">
              {isSuccess ? "Check your email" : "Reset your password"}
            </CardTitle>
            <p className="text-gray-light mt-1">
              {isSuccess
                ? "If an account exists with that email, a password reset link has been sent."
                : "Enter your email to receive a reset link"}
            </p>
          </CardHeader>
          <CardContent>
            {isSuccess ? (
              <div className="text-center py-4">
                <div className="text-4xl mb-4">✓</div>
                <p className="text-gray-light mb-4">
                  Please check your email and click the link to reset your password.
                </p>
                <Link
                  to="/login"
                  className="text-primary hover:text-lime-hover font-medium"
                >
                  Back to Sign In
                </Link>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-gray-light">Email</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all"
                  >
                    {isSubmitting ? "Sending..." : "Send reset link"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <Link to="/login" className="text-gray-light hover:text-white text-sm">
                    ← Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      <MarketingFooter />
    </>
  );
}


